/**
 * MentionAutocomplete Component
 * Dropdown for @ user/special-mention selection with search filtering
 * Triggered when user types @ in MessageInput
 *
 * Shows special mentions (@everyone, @here) in channel context,
 * followed by matching users.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { User } from '@/types/api.types';
import { AtSign, X, Megaphone, Globe } from 'lucide-react';

// Discriminated union for all autocomplete items
type AutocompleteItem =
  | { kind: 'special'; type: 'everyone' | 'here'; label: string; description: string }
  | { kind: 'user'; user: User };

export interface MentionSelection {
  userId?: string;
  displayName: string;
  type?: 'everyone' | 'here';
}

interface MentionAutocompleteProps {
  users: User[];
  query: string;
  onSelect: (mention: MentionSelection) => void;
  onClose: () => void;
  position?: { top: number; left: number };
  /** When true, show @everyone and @here options (only relevant in channels) */
  channelContext?: boolean;
}

// Special mention definitions
const SPECIAL_MENTIONS: Array<{ type: 'everyone' | 'here'; label: string; aliases: string[]; description: string }> = [
  { type: 'everyone', label: '@everyone', aliases: ['everyone', 'всички'], description: 'Уведоми всички' },
  { type: 'here', label: '@here', aliases: ['here'], description: 'Уведоми онлайн потребителите в канала' },
];

export const MentionAutocomplete: React.FC<MentionAutocompleteProps> = ({
  users,
  query,
  onSelect,
  onClose,
  position,
  channelContext = false,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Build unified items list: special mentions first (if channel context), then users
  const items = useMemo<AutocompleteItem[]>(() => {
    const result: AutocompleteItem[] = [];
    const searchText = query.toLowerCase();

    if (channelContext) {
      for (const special of SPECIAL_MENTIONS) {
        if (!searchText || special.aliases.some((a) => a.startsWith(searchText)) || special.label.toLowerCase().includes(searchText)) {
          result.push({ kind: 'special', type: special.type, label: special.label, description: special.description });
        }
      }
    }

    // Filter users based on query
    const filteredUsers = users.filter((user) => (
      (user.display_name || '').toLowerCase().includes(searchText) ||
      (user.email || '').toLowerCase().includes(searchText) ||
      `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase().includes(searchText)
    ));

    for (const user of filteredUsers) {
      result.push({ kind: 'user', user });
    }

    return result;
  }, [channelContext, query, users]);

  // Reset selected index when the query changes (adjust state during render)
  const [prevQuery, setPrevQuery] = useState(query);
  if (query !== prevQuery) {
    setPrevQuery(query);
    setSelectedIndex(0);
  }

  // Handle item selection
  const handleItemSelect = useCallback(
    (item: AutocompleteItem) => {
      if (item.kind === 'special') {
        onSelect({ displayName: item.type === 'everyone' ? 'everyone' : 'here', type: item.type });
      } else {
        onSelect({ userId: item.user.id, displayName: item.user.display_name });
      }
    },
    [onSelect]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (items.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, items.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          handleItemSelect(items[selectedIndex]);
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [items, selectedIndex, onClose, handleItemSelect]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  if (items.length === 0) {
    return (
      <div
        ref={dropdownRef}
        className="absolute z-50 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3"
        style={position}
      >
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <span>No users found</span>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={dropdownRef}
      className="absolute z-50 w-80 max-h-64 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
      style={position}
    >
      <div className="p-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <AtSign className="w-4 h-4" />
          <span>Mention someone</span>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="py-1" role="listbox" aria-label="Споменавания">
        {items.map((item, index) => {
          if (item.kind === 'special') {
            return (
              <button
                key={`special-${item.type}`}
                role="option"
                id={`mention-option-${index}`}
                aria-selected={index === selectedIndex}
                onClick={() => handleItemSelect(item)}
                className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  index === selectedIndex
                    ? 'bg-amber-50 dark:bg-amber-900/20 border-l-2 border-amber-500'
                    : ''
                }`}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {/* Icon */}
                <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500 flex-shrink-0">
                  {item.type === 'everyone' ? <Megaphone className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
                </div>

                {/* Info */}
                <div className="flex-1 text-left overflow-hidden">
                  <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                    {item.label}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {item.description}
                  </div>
                </div>

                {/* Selected indicator */}
                {index === selectedIndex && (
                  <div className="text-amber-500">
                    <AtSign className="w-4 h-4" />
                  </div>
                )}
              </button>
            );
          }

          // User item
          const user = item.user;
          return (
            <button
              key={user.id}
              role="option"
              id={`mention-option-${index}`}
              aria-selected={index === selectedIndex}
              onClick={() => handleItemSelect(item)}
              className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                index === selectedIndex
                  ? 'bg-amber-50 dark:bg-amber-900/20 border-l-2 border-amber-500'
                  : ''
              }`}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              {/* Avatar */}
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                {user.display_name.charAt(0).toUpperCase()}
              </div>

              {/* User info */}
              <div className="flex-1 text-left overflow-hidden">
                <div className="flex items-center gap-1.5 font-medium text-gray-900 dark:text-gray-100 truncate">
                  {user.display_name}
                  {user.is_bot && (
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                      AI
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user.is_bot ? `@${user.display_name?.toLowerCase().replace(/\s+/g, '-')}` : user.email}
                </div>
              </div>

              {/* Selected indicator */}
              {index === selectedIndex && (
                <div className="text-amber-500">
                  <AtSign className="w-4 h-4" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="p-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center justify-between">
          <span>↑↓ Navigate</span>
          <span>↵ Select</span>
          <span>Esc Close</span>
        </div>
      </div>
    </div>
  );
};
